import { Space } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { CompanySearchField } from '@features/companies/elements/CompanySearchField'
import { InvoiceLogo, type UploadZervantInvoiceLogoResponse } from '@features/invoicingSettings/modules/logoAndBranding'

import { OnboardingInvoices } from '../../../../../../assets/images'
import { useDebouncedCallback } from '../../../../../../hooks/useDebouncedCallback'
import { useOnboardingFormContext } from '../../../../contexts/OnboardingFormContext'
import { useRegisterOnboardingStep } from '../../../../hooks/useRegisterOnboardingStep'
import { useTrackOnboarding } from '../../../../hooks/useTrackOnboarding'
import { OnboardingForm } from '../../../../types/onboardingForm'
import { StepContainer } from '../StepContainer'
import { usePredefinedInvoicesStepValues } from './hooks/usePredefinedInvoicesStepValues'
import { useSubmitInvoicesStepForm } from './hooks/useSubmitInvoicesStepForm'
import { getCompanyDetails } from './utils/getCompanyDetails'

const INVOICE_LOGO_MAX_SIZE = 5

export const InvoicesStep = (): ReactElement => {
  const { t } = useTranslation()

  const { onboardingForm, isLoading } = useOnboardingFormContext()
  const { FormItem } = onboardingForm

  const { isLoading: isLoadingPredefinedValues, predefinedValues } = usePredefinedInvoicesStepValues()
  const { isSubmitting, submitForm } = useSubmitInvoicesStepForm()

  const { trackOnboarding } = useTrackOnboarding()
  const handleDebouncedCompanyNameTyped = useDebouncedCallback(() => trackOnboarding('Company name typed'))

  const submitHandler = useCallback(
    async (values: OnboardingForm) => {
      await submitForm(values)
    },
    [submitForm],
  )

  useRegisterOnboardingStep({
    isLoading: isLoadingPredefinedValues,
    isSubmitting,
    predefinedValues,
    submitHandler,
  })

  return (
    <StepContainer image={<OnboardingInvoices />} title={t('onboarding.steps.invoices.title')}>
      <FormItem
        label={t('onboarding.steps.invoices.company_name.label')}
        name="company"
        render={({ field: { onChange, value, ...rest } }) => (
          <CompanySearchField
            {...rest}
            autoFocus={false}
            disabled={isLoading}
            onChange={(companyName) => {
              // Reset company details when user types to avoid mismatch between company name and other details
              // autofilled from the previously selected company
              onChange({
                city: '',
                legalStatus: undefined,
                name: companyName,
                registrationNo: '',
                street: '',
                vatNo: '',
                zipcode: '',
              })
              handleDebouncedCompanyNameTyped()
            }}
            onSelectCompany={(company) => {
              const companyDetails = getCompanyDetails(company)
              onChange(companyDetails)
            }}
            value={value.name}
          />
        )}
      />
      <Space size="s16" />
      <FormItem
        label={t('onboarding.steps.invoices.company_logo.label')}
        name="companyLogo"
        render={({ field: { onChange, value } }) => (
          <InvoiceLogo
            contentText={t('onboarding.steps.invoices.logo.content_text')}
            footerText={t('onboarding.steps.invoices.logo.footer_text', { maxSize: INVOICE_LOGO_MAX_SIZE })}
            isLoading={isLoading}
            logoUrl={value?.url}
            onDeleteSuccess={() => onChange(null)}
            onUploadSuccess={(data: UploadZervantInvoiceLogoResponse) => {
              trackOnboarding('Company logo clicked')
              onChange({ fileId: String(data.logoId), url: data.url })
            }}
          />
        )}
      />
    </StepContainer>
  )
}
